import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../utils/auth';

/**
 * Recovery for subscriptions RevenueCat attributed to nobody.
 *
 * An App Store offer code redeemed outside the app — from an
 * `apps.apple.com/redeem?ctx=offercodes` link — is redeemed against the Apple ID
 * before FRAW is necessarily installed, so the receipt is posted while the RevenueCat
 * SDK is still anonymous and the purchase lands on a customer called
 * `$RCAnonymousID:…`. No FRAW user owns that id, so the webhook cannot grant anything,
 * and RevenueCat never re-sends the event under the real id once the customer is
 * merged. The app claims its own anonymous ids automatically after login, which covers
 * everyone from that build onward; this page exists for the accounts that broke before
 * it shipped, whose device no longer knows which id it used.
 *
 * Two lists, because the two eras leave different evidence:
 *  - Parked purchases keep the whole event, so attributing them replays it exactly.
 *  - Historical deliveries lost their payload, so attribution instead asks RevenueCat
 *    what the identity currently owns and grants that.
 */

interface OrphanedPurchase {
  _id: string;
  eventId: string;
  rcAppUserId: string;
  eventType: string;
  transactionId?: string;
  originalTransactionId?: string;
  productId?: string;
  offerCode?: string;
  store?: string;
  environment?: string;
  purchasedAt?: string;
  expiresAt?: string;
  status: 'unclaimed' | 'claimed';
  claimedByUserId?: string;
  claimedAt?: string;
  lastError?: string;
  createdAt: string;
}

interface HistoricalOrphan {
  rcAppUserId: string;
  transactionId?: string;
  eventTypes: string[];
  deliveries: number;
  firstSeen: string;
  lastSeen: string;
  mappedToUserId?: string;
}

interface RcIdentityRow {
  _id: string;
  rcAppUserId: string;
  userId: string;
  source: string;
  note?: string;
  createdAt: string;
}

interface UserMatch {
  userId: string;
  email?: string;
  name?: string;
  plan?: string;
}

interface AttributionResult {
  rcAppUserId: string;
  userId: string;
  recovered: number;
  failed: number;
  invalid: number;
  liveGrant?: string;
  liveGrantReason?: string;
  planBefore?: string;
  planAfter?: string;
}

const PANEL: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '20px',
};

const INPUT: React.CSSProperties = {
  padding: '7px 10px',
  backgroundColor: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  color: 'var(--color-text)',
  fontSize: '14px',
};

const TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  color: 'var(--color-muted)',
  fontSize: '12px',
  fontWeight: 600,
  borderBottom: '1px solid var(--color-border)',
  whiteSpace: 'nowrap',
};

const TD: React.CSSProperties = {
  padding: '8px 10px',
  color: 'var(--color-text)',
  fontSize: '13px',
  borderBottom: '1px solid var(--color-surface-hover)',
  verticalAlign: 'top',
};

/** One field per row, so no control can look like it belongs to the whole form. */
const FIELD_WIDTH = '620px';

const STEP: React.CSSProperties = {
  marginBottom: '18px',
};

const STEP_LABEL: React.CSSProperties = {
  color: 'var(--color-text)',
  fontSize: '13px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '6px',
};

const STEP_NUMBER: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-surface-hover)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-muted)',
  fontSize: '11px',
  fontWeight: 600,
  flexShrink: 0,
};

const HELP: React.CSSProperties = {
  color: 'var(--color-muted)',
  fontSize: '12px',
  margin: '6px 0 0',
};

const MONO: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '12px',
  wordBreak: 'break-all',
};

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '—';

/**
 * How each `liveGrant` outcome should read to whoever is working the queue. The
 * distinction that matters most is `unavailable` vs `not_found`: being unable to ask
 * RevenueCat is not evidence that there is nothing to recover, and treating it as such
 * is how an account gets written off as already clean.
 */
const LIVE_GRANT_COPY: Record<string, { text: string; color: string }> = {
  applied: { text: 'RevenueCat confirmed a live subscription — plan and credits granted', color: 'var(--color-success-strong)' },
  not_found: { text: 'No live subscription on any mapped identity — nothing granted', color: 'var(--color-subtle)' },
  unavailable: { text: 'RevenueCat could not be reached — retry, this is not "nothing to recover"', color: 'var(--color-warning)' },
  unsupported_store: { text: 'The entitlement is a web/Stripe one, which this path must not grant', color: 'var(--color-warning)' },
  unknown_product: { text: 'The entitlement names a product with no plan mapping', color: 'var(--color-warning)' },
  billing_suspended: { text: 'Account is in dunning — resolve the outstanding payment first', color: 'var(--color-warning)' },
};

export default function RevenueCatRecoveryPage() {
  const [parked, setParked] = useState<OrphanedPurchase[]>([]);
  const [historical, setHistorical] = useState<HistoricalOrphan[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [parkedStatus, setParkedStatus] = useState<'unclaimed' | 'claimed' | 'all'>('unclaimed');
  const [hideMapped, setHideMapped] = useState(true);

  // Attribution form
  const [rcAppUserId, setRcAppUserId] = useState('');
  const [userId, setUserId] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AttributionResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // User lookup
  const [userQuery, setUserQuery] = useState('');
  const [matches, setMatches] = useState<UserMatch[]>([]);
  const [searching, setSearching] = useState(false);
  const [identities, setIdentities] = useState<RcIdentityRow[] | null>(null);

  /**
   * The RevenueCat secret key, held in memory for as long as this tab is open and sent
   * with each request. Deliberately not persisted anywhere — not in localStorage, not on
   * the server — so it disappears on reload and leaves no copy behind.
   */
  const [apiKey, setApiKey] = useState('');

  // Omitted entirely when empty, so the backend falls back to its own env var rather
  // than being handed a blank credential.
  const keyHeader = (): Record<string, string> =>
    apiKey.trim() ? { 'X-RevenueCat-Api-Key': apiKey.trim() } : {};

  const fetchLists = useCallback(async () => {
    try {
      setLoading(true);
      setListError(null);
      const [parkedRes, historicalRes] = await Promise.all([
        apiRequest<{ success: boolean; data: { data: OrphanedPurchase[] } }>(
          `/api/admin/orphaned-purchases?status=${parkedStatus}&limit=100`,
        ),
        apiRequest<{ success: boolean; data: { data: HistoricalOrphan[] } }>(
          `/api/admin/orphaned-webhook-events?limit=100&unmappedOnly=${hideMapped}`,
        ),
      ]);
      setParked(parkedRes.data?.data || []);
      setHistorical(historicalRes.data?.data || []);
    } catch (err: any) {
      setListError(err.message || 'Failed to load orphaned purchases');
    } finally {
      setLoading(false);
    }
  }, [parkedStatus, hideMapped]);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const searchUsers = async () => {
    const q = userQuery.trim();
    if (!q) return;
    try {
      setSearching(true);
      setFormError(null);
      const res = await apiRequest<{ success: boolean; data: UserMatch[] }>(
        `/api/admin/users?limit=10&search=${encodeURIComponent(q)}`,
      );
      setMatches(res.data || []);
    } catch (err: any) {
      setFormError(err.message || 'User search failed');
    } finally {
      setSearching(false);
    }
  };

  const loadIdentities = async (id: string) => {
    try {
      const res = await apiRequest<{ success: boolean; data: { data: RcIdentityRow[] } }>(
        `/api/admin/users/${encodeURIComponent(id)}/rc-identities`,
      );
      setIdentities(res.data?.data || []);
    } catch {
      setIdentities(null);
    }
  };

  const pickUser = (u: UserMatch) => {
    setUserId(u.userId);
    setMatches([]);
    setUserQuery(u.email || u.userId);
    loadIdentities(u.userId);
  };

  const attribute = async () => {
    const identity = rcAppUserId.trim();
    const target = userId.trim();
    if (!identity || !target) {
      setFormError('Both a RevenueCat anonymous ID and a user are required');
      return;
    }
    try {
      setSubmitting(true);
      setFormError(null);
      setResult(null);
      const res = await apiRequest<{ success: boolean; data: AttributionResult }>(
        `/api/admin/users/${encodeURIComponent(target)}/rc-identity`,
        {
          method: 'POST',
          headers: keyHeader(),
          body: JSON.stringify({ rcAppUserId: identity, note: note.trim() || undefined }),
        },
      );
      setResult(res.data);
      await Promise.all([fetchLists(), loadIdentities(target)]);
    } catch (err: any) {
      setFormError(err.message || 'Attribution failed');
    } finally {
      setSubmitting(false);
    }
  };

  const prefill = (identity: string) => {
    setRcAppUserId(identity);
    setResult(null);
    setFormError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const badge = (text: string, color: string) => (
    <span style={{
      padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600,
      backgroundColor: color, color: 'white', whiteSpace: 'nowrap',
    }}>{text}</span>
  );

  return (
    <div className="page">
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ color: 'var(--color-text)', margin: 0, fontSize: '24px' }}>RevenueCat Recovery</h1>
        <p style={{ color: 'var(--color-muted)', marginTop: '4px', fontSize: '14px', maxWidth: '860px' }}>
          Subscriptions attributed to an anonymous RevenueCat customer instead of a FRAW user —
          what an App Store offer code redeemed outside the app produces. Attributing the identity
          replays the parked purchase, and where there is nothing to replay it asks RevenueCat what
          the identity owns and grants that. The mapping is permanent and one owner per identity, so
          confirm the redemption really is that user's before writing it.
        </p>
      </div>

      {/* ---- Key ---- */}
      <div style={PANEL}>
        <h2 style={{ color: 'var(--color-text)', margin: '0 0 4px', fontSize: '16px' }}>RevenueCat secret key</h2>
        <p style={{ color: 'var(--color-muted)', margin: '0 0 14px', fontSize: '13px', maxWidth: '860px' }}>
          Needed only if the backend has no <code>REVENUECAT_API_KEY</code>. Paste it here and it is
          sent with each request on this page — <strong>nothing is stored</strong>, on the server or in
          this browser, so it is gone when you reload the tab. Get it from RevenueCat → Project
          settings → API keys → <strong>+ New secret API key, version V1</strong> (a V2 key cannot read
          the <code>/v1</code> endpoint the backend calls, and a public SDK key is rejected).
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="sk_…"
            autoComplete="off"
            spellCheck={false}
            style={{ ...INPUT, flex: '1 1 340px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
          />
          {apiKey.trim() && (
            <button
              onClick={() => setApiKey('')}
              style={{ padding: '7px 14px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
            >
              Clear
            </button>
          )}
          <span style={{ color: apiKey.trim() ? 'var(--color-success)' : 'var(--color-muted)', fontSize: '13px' }}>
            {apiKey.trim()
              ? 'Will be used when you attribute an identity below'
              : 'Not set — the backend will use its own key if it has one'}
          </span>
        </div>

        <p style={{ color: 'var(--color-muted)', margin: '12px 0 0', fontSize: '12px', maxWidth: '860px' }}>
          This unblocks recovery from this page only. The in-app <em>Restore Purchases</em> flow and the
          plan-upgrade check run without an admin present, so they still need
          <code> REVENUECAT_API_KEY</code> configured on the backend.
        </p>
      </div>

      {/* ---- Attribution ---- */}
      <div style={PANEL}>
        <h2 style={{ color: 'var(--color-text)', margin: '0 0 4px', fontSize: '16px' }}>Attribute an identity</h2>
        <p style={{ color: 'var(--color-muted)', margin: '0 0 18px', fontSize: '13px', maxWidth: '860px' }}>
          Find the buyer in App Store Connect from the transaction ID, or match the offer code against
          who it was sent to, and check the redemption time against the account's signup time. The
          mapping is permanent and one owner per identity, so confirm before writing it.
        </p>

        {/* Step 1 — the identity */}
        <div style={STEP}>
          <label style={STEP_LABEL}><span style={STEP_NUMBER}>1</span> RevenueCat anonymous ID</label>
          <input
            type="text"
            value={rcAppUserId}
            onChange={(e) => setRcAppUserId(e.target.value)}
            placeholder="$RCAnonymousID:…"
            spellCheck={false}
            style={{ ...INPUT, ...MONO, width: '100%', maxWidth: FIELD_WIDTH }}
          />
          <p style={HELP}>
            Or press <strong>Attribute…</strong> on any row in the tables below to fill this in.
          </p>
        </div>

        {/* Step 2 — the owner. Search and its results live here, so it is obvious the
            button belongs to this field and not to the form as a whole. */}
        <div style={STEP}>
          <label style={STEP_LABEL}><span style={STEP_NUMBER}>2</span> The user who owns it</label>
          <div style={{ display: 'flex', gap: '8px', width: '100%', maxWidth: FIELD_WIDTH }}>
            <input
              type="text"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
              placeholder="Search by email or name"
              style={{ ...INPUT, flex: 1 }}
            />
            <button
              onClick={searchUsers}
              disabled={searching || !userQuery.trim()}
              style={{ padding: '7px 16px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: searching || !userQuery.trim() ? 'not-allowed' : 'pointer', fontSize: '14px', whiteSpace: 'nowrap' }}
            >
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>

          {matches.length > 0 && (
            <>
              <p style={HELP}>Click the account to select it:</p>
              <div style={{ border: '1px solid var(--color-border)', borderRadius: '6px', overflow: 'hidden', maxWidth: FIELD_WIDTH }}>
                {matches.map((u) => (
                  <button
                    key={u.userId}
                    onClick={() => pickUser(u)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                      backgroundColor: 'var(--color-bg)', color: 'var(--color-text)', border: 'none',
                      borderBottom: '1px solid var(--color-surface-hover)', cursor: 'pointer', fontSize: '13px',
                    }}
                  >
                    {u.email || '(no email)'} · {u.name || '(no name)'} · plan {u.plan || '—'}
                    <span style={{ ...MONO, color: 'var(--color-muted)', display: 'block' }}>{u.userId}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {userId ? (
            <div style={{ marginTop: '8px', padding: '8px 10px', backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-success-strong)', borderRadius: '6px', fontSize: '13px', color: 'var(--color-text)', maxWidth: FIELD_WIDTH }}>
              Selected: <span style={{ ...MONO, color: 'var(--color-success)' }}>{userId}</span>
              {identities && identities.length > 0 && (
                <div style={{ color: 'var(--color-muted)', marginTop: '4px', fontSize: '12px' }}>
                  Already holds {identities.length} mapped {identities.length === 1 ? 'identity' : 'identities'}:{' '}
                  <span style={MONO}>{identities.map(i => i.rcAppUserId).join(', ')}</span>
                </div>
              )}
            </div>
          ) : (
            <p style={HELP}>No account selected yet — search, then click a result.</p>
          )}
        </div>

        {/* Step 3 — audit trail */}
        <div style={STEP}>
          <label style={STEP_LABEL}><span style={STEP_NUMBER}>3</span> Note <span style={{ fontWeight: 400 }}>(optional)</span></label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="support ticket 1234"
            style={{ ...INPUT, width: '100%', maxWidth: FIELD_WIDTH }}
          />
          <p style={HELP}>Stored on the mapping, so later you can tell why it was made.</p>
        </div>

        <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '16px', marginTop: '4px' }}>
          <button
            onClick={attribute}
            disabled={submitting || !rcAppUserId.trim() || !userId.trim()}
            style={{
              padding: '9px 20px',
              backgroundColor: submitting || !rcAppUserId.trim() || !userId.trim() ? 'var(--color-surface-hover)' : 'var(--color-accent-strong)',
              color: submitting || !rcAppUserId.trim() || !userId.trim() ? 'var(--color-subtle)' : 'white',
              border: 'none', borderRadius: '6px',
              cursor: submitting || !rcAppUserId.trim() || !userId.trim() ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 500,
            }}
          >
            {submitting ? 'Attributing…' : 'Attribute & recover'}
          </button>
          {(!rcAppUserId.trim() || !userId.trim()) && (
            <span style={{ color: 'var(--color-muted)', fontSize: '12px', marginLeft: '10px' }}>
              Needs {!rcAppUserId.trim() ? 'an anonymous ID' : ''}
              {!rcAppUserId.trim() && !userId.trim() ? ' and ' : ''}
              {!userId.trim() ? 'a selected user' : ''}
            </span>
          )}
        </div>

        {formError && (
          <div style={{ marginTop: '12px', padding: '10px 12px', backgroundColor: '#2d1214', border: '1px solid var(--color-danger-strong)', borderRadius: '6px', color: '#ff7b72', fontSize: '13px' }}>
            {formError}
          </div>
        )}

        {result && (
          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', color: 'var(--color-text)' }}>
            <div style={{ marginBottom: '6px' }}>
              Mapped <span style={MONO}>{result.rcAppUserId}</span> → <span style={MONO}>{result.userId}</span>
            </div>
            <div style={{ marginBottom: '6px' }}>
              Plan: <strong>{result.planBefore || '—'}</strong> → <strong>{result.planAfter || '—'}</strong>
              {' · '}parked events replayed: <strong>{result.recovered}</strong>
              {result.failed > 0 && <span style={{ color: 'var(--color-warning)' }}> · {result.failed} replay failure(s)</span>}
              {result.invalid > 0 && <span style={{ color: 'var(--color-warning)' }}> · {result.invalid} unparseable</span>}
            </div>
            {result.liveGrant && (
              <div style={{ color: LIVE_GRANT_COPY[result.liveGrant]?.color || 'var(--color-muted)' }}>
                {LIVE_GRANT_COPY[result.liveGrant]?.text || result.liveGrant}
                {result.liveGrantReason === 'missing_api_key' && (
                  <div style={{ marginTop: '4px', color: '#ff7b72' }}>
                    No key was available — paste one in the field at the top of this page, or set
                    REVENUECAT_API_KEY on the backend.
                  </div>
                )}
                {result.liveGrantReason && result.liveGrantReason !== 'missing_api_key' && (
                  <div style={{ marginTop: '4px', color: 'var(--color-muted)' }}>reason: {result.liveGrantReason}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {listError && (
        <div style={{ ...PANEL, backgroundColor: '#2d1214', borderColor: 'var(--color-danger-strong)', color: '#ff7b72' }}>
          {listError}
        </div>
      )}

      {/* ---- Parked purchases ---- */}
      <div style={PANEL}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
          <div>
            <h2 style={{ color: 'var(--color-text)', margin: 0, fontSize: '16px' }}>Parked purchases</h2>
            <p style={{ color: 'var(--color-muted)', margin: '4px 0 0', fontSize: '13px' }}>
              The full event was kept, so attributing these replays the real purchase.
            </p>
          </div>
          <select
            value={parkedStatus}
            onChange={(e) => setParkedStatus(e.target.value as typeof parkedStatus)}
            style={INPUT}
          >
            <option value="unclaimed">Unclaimed</option>
            <option value="claimed">Claimed</option>
            <option value="all">All</option>
          </select>
        </div>

        {loading ? (
          <div style={{ color: 'var(--color-muted)', fontSize: '13px' }}>Loading…</div>
        ) : parked.length === 0 ? (
          <div style={{ color: 'var(--color-muted)', fontSize: '13px' }}>Nothing parked.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>Purchased</th>
                  <th style={TH}>Product</th>
                  <th style={TH}>Offer code</th>
                  <th style={TH}>Event</th>
                  <th style={TH}>Anonymous ID</th>
                  <th style={TH}>Apple transaction</th>
                  <th style={TH}>Status</th>
                  <th style={TH}></th>
                </tr>
              </thead>
              <tbody>
                {parked.map((row) => (
                  <tr key={row._id}>
                    <td style={TD}>{formatDate(row.purchasedAt || row.createdAt)}</td>
                    <td style={TD}>{row.productId || '—'}</td>
                    <td style={TD}>{row.offerCode || '—'}</td>
                    <td style={TD}>{row.eventType}{row.environment === 'SANDBOX' && ' (sandbox)'}</td>
                    <td style={{ ...TD, ...MONO }}>{row.rcAppUserId}</td>
                    <td style={{ ...TD, ...MONO }}>{row.originalTransactionId || row.transactionId || '—'}</td>
                    <td style={TD}>
                      {row.status === 'claimed'
                        ? badge('claimed', 'var(--color-success-strong)')
                        : badge('unclaimed', 'var(--color-warning)')}
                      {row.lastError && (
                        <div style={{ color: '#ff7b72', fontSize: '12px', marginTop: '4px' }}>{row.lastError}</div>
                      )}
                      {row.claimedByUserId && (
                        <div style={{ ...MONO, color: 'var(--color-muted)', marginTop: '4px' }}>{row.claimedByUserId}</div>
                      )}
                    </td>
                    <td style={TD}>
                      {row.status === 'unclaimed' && (
                        <button
                          onClick={() => prefill(row.rcAppUserId)}
                          style={{ padding: '5px 10px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}
                        >
                          Attribute…
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---- Historical orphans ---- */}
      <div style={PANEL}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
          <div>
            <h2 style={{ color: 'var(--color-text)', margin: 0, fontSize: '16px' }}>Historical orphans (payload not kept)</h2>
            <p style={{ color: 'var(--color-muted)', margin: '4px 0 0', fontSize: '13px' }}>
              Deliveries from before purchases were parked. There is nothing to replay, so attribution
              asks RevenueCat what the identity owns — one row per subscription.
            </p>
          </div>
          <label style={{ color: 'var(--color-muted)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input type="checkbox" checked={hideMapped} onChange={(e) => setHideMapped(e.target.checked)} />
            Hide already mapped
          </label>
        </div>

        {loading ? (
          <div style={{ color: 'var(--color-muted)', fontSize: '13px' }}>Loading…</div>
        ) : historical.length === 0 ? (
          <div style={{ color: 'var(--color-muted)', fontSize: '13px' }}>
            {hideMapped ? 'No unmapped historical orphans.' : 'No historical orphans.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>First seen</th>
                  <th style={TH}>Last seen</th>
                  <th style={TH}>Events</th>
                  <th style={TH}>Anonymous ID</th>
                  <th style={TH}>Apple transaction</th>
                  <th style={TH}>Mapped to</th>
                  <th style={TH}></th>
                </tr>
              </thead>
              <tbody>
                {historical.map((row) => (
                  <tr key={`${row.rcAppUserId}:${row.transactionId || ''}`}>
                    <td style={TD}>{formatDate(row.firstSeen)}</td>
                    <td style={TD}>{formatDate(row.lastSeen)}</td>
                    <td style={TD}>
                      {row.eventTypes.join(', ')}
                      {row.deliveries > row.eventTypes.length && (
                        <span style={{ color: 'var(--color-muted)' }}> ({row.deliveries} deliveries)</span>
                      )}
                    </td>
                    <td style={{ ...TD, ...MONO }}>{row.rcAppUserId}</td>
                    <td style={{ ...TD, ...MONO }}>{row.transactionId || '—'}</td>
                    <td style={TD}>
                      {row.mappedToUserId
                        ? <span style={MONO}>{row.mappedToUserId}</span>
                        : badge('unmapped', 'var(--color-warning)')}
                    </td>
                    <td style={TD}>
                      {!row.mappedToUserId && (
                        <button
                          onClick={() => prefill(row.rcAppUserId)}
                          style={{ padding: '5px 10px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}
                        >
                          Attribute…
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
